// frontend/src/pages/ArticlesPage.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import LanguageBadge from "../components/common/LanguageBadge";

const ArticlesPage = () => {
  const { isAuthenticated, hasAccess } = useAuth();
  const { language, t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    // Reset and fetch initial articles when language changes
    setArticles([]);
    setOffset(0);
    setHasMore(true);
    fetchArticles(0, true);
  }, [language]); // Only re-fetch when language changes, not auth state

  useEffect(() => {
    // Add scroll event listener for infinite scroll
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Load more when user scrolls to 80% of the page
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        loadMoreArticles();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore]); // Removed offset from dependencies to prevent re-triggering

  const fetchArticles = async (newOffset = 0, isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = isAuthenticated
        ? await apiService.getMyArticles(language, newOffset)
        : await apiService.getArticles(language, newOffset);

      if (response.success) {
        const newArticles = response.data || [];

        if (isInitial) {
          setArticles(newArticles);
          setOffset(newArticles.length);
        } else {
          // Filter out duplicates by ID
          setArticles(prev => {
            const existingIds = new Set(prev.map(a => a.id));
            const uniqueNew = newArticles.filter(a => !existingIds.has(a.id));
            return [...prev, ...uniqueNew];
          });
          // Use callback to get latest state
          setOffset(prevOffset => prevOffset + newArticles.length);
        }
        setHasMore(response.pagination?.hasMore || false);
      } else {
        setError("Failed to load articles");
      }
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError("Failed to load articles. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreArticles = () => {
    if (!loadingMore && hasMore) {
      fetchArticles(offset, false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const cleanText = (text) => {
    return text?.replace(/<br\s*\/?>/gi, ' ').trim() || '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchArticles}
            className="bg-[#00A3E0] text-white px-4 py-2 rounded-md hover:bg-[#002855]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Split articles into sections
  const featuredArticle = articles[0];
  const latestArticles = articles.slice(1, 4);
  const remainingArticles = articles.slice(4);

  const handleArticleClick = (article, e) => {
    if (article.news_type === 'url' && article.urlDE) {
      e.preventDefault();
      window.open(article.urlDE, '_blank');
    }
  };

  const getArticleLink = (article) => {
    if (article.news_type === 'url' && article.urlDE) {
      return '#';
    }
    return `/article/${article.slug || article.id}`;
  };

  const ArticleImage = ({ article, className = "" }) => {
    const imageUrl = article.image;

    return imageUrl ? (
      <img
        src={imageUrl.startsWith('http')
          ? imageUrl
          : `https://www.burnair.cloud/images/news/${imageUrl}`}
        alt={cleanText(article.title)}
        className={`object-cover ${className}`}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      <div className={`bg-gradient-to-br from-[#00A3E0] via-[#84BD00] to-[#002855] ${className}`} />
    );
  };

  const ContractBadge = ({ contract }) => {
    if (!contract || contract === 'Free') return null;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold text-white rounded ${
        contract === 'Pro' ? 'bg-black' :
        contract === 'Premium' ? 'bg-[#FC0058]' :
        'bg-[#23CEE7]'
      }`}>
        {contract.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {articles.length > 0 ? (
          <>
            {/* Featured Article - Hero Section */}
            {featuredArticle && (() => {
              const canAccess = hasAccess(featuredArticle.contract || 'Free');
              return (
                <Link
                  to={canAccess ? getArticleLink(featuredArticle) : "#"}
                  className={`block mb-8 group ${!canAccess ? 'cursor-not-allowed' : ''}`}
                  onClick={(e) => {
                    if (canAccess) {
                      handleArticleClick(featuredArticle, e);
                    } else {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className={`relative bg-white rounded-lg shadow-lg overflow-hidden transition-shadow duration-300 ${canAccess ? 'hover:shadow-xl' : 'opacity-75'}`}>
                    <div className="flex flex-col md:flex-row gap-0">
                      {/* RULE: Featured article image MUST BE square and NEVER bigger than 400x400px on desktop */}
                      <div className="relative aspect-square w-full md:w-auto md:max-w-[400px] md:max-h-[400px] md:flex-shrink-0">
                        <ArticleImage article={featuredArticle} className="w-full h-full" />
                        {!canAccess && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <Lock className="w-12 h-12 text-white" />
                          </div>
                        )}
                        <div className="absolute top-4 left-4 flex gap-2">
                          <ContractBadge contract={featuredArticle.contract} />
                          {featuredArticle.article_read === false && (
                            <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-600 rounded">
                              {t({ en: "NEW", de: "NEU" })}
                            </span>
                          )}
                        </div>
                      </div>
                    <div className="p-8 flex flex-col justify-start">
                      <div className="text-sm text-[#00A3E0] font-bold mb-3 uppercase tracking-wide">
                        {t({ en: "Featured", de: "Highlight" })}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-[#00A3E0] transition-colors">
                        {cleanText(featuredArticle.title)}
                      </h2>
                      {featuredArticle.description && (
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {cleanText(featuredArticle.description)}
                        </p>
                      )}
                      <div className="text-sm text-gray-500">
                        {formatDate(featuredArticle.activation)}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
            })()}

            {/* Latest 3 Articles */}
            {latestArticles.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {t({ en: "Latest", de: "Aktuell" })}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {latestArticles.map((article) => {
                    const canAccess = hasAccess(article.contract || 'Free');
                    return (
                      <Link
                        key={article.id}
                        to={canAccess ? getArticleLink(article) : "#"}
                        className={`block group ${!canAccess ? 'cursor-not-allowed' : ''}`}
                        onClick={(e) => {
                          if (canAccess) {
                            handleArticleClick(article, e);
                          } else {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className={`bg-white rounded-lg shadow transition-shadow duration-300 overflow-hidden h-full flex flex-col ${canAccess ? 'hover:shadow-lg' : 'opacity-75'}`}>
                          <div className="aspect-square relative flex-shrink-0">
                            <ArticleImage article={article} className="w-full h-full" />
                            {!canAccess && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                <Lock className="w-8 h-8 text-white" />
                              </div>
                            )}
                          </div>
                        <div className="p-4 flex flex-col flex-grow">
                          <div className="flex-grow">
                            <h3 className="font-bold text-gray-900 group-hover:text-[#00A3E0] transition-colors line-clamp-2">
                              {cleanText(article.title)}
                            </h3>
                            {article.description && (
                              <p className="text-gray-600 mt-0.5 line-clamp-2">
                                {cleanText(article.description)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                            <span>{formatDate(article.activation)}</span>
                            <LanguageBadge language="de" />
                            <ContractBadge contract={article.contract} />
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                  })}
                </div>
              </div>
            )}

            {/* All Other Articles - Instagram-like Grid */}
            {remainingArticles.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {t({ en: "More Articles", de: "Weitere Artikel" })}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {remainingArticles.map((article) => {
                    const canAccess = hasAccess(article.contract || 'Free');
                    return (
                      <Link
                        key={article.id}
                        to={canAccess ? getArticleLink(article) : "#"}
                        className={`block group ${!canAccess ? 'cursor-not-allowed' : ''}`}
                        onClick={(e) => {
                          if (canAccess) {
                            handleArticleClick(article, e);
                          } else {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className={`bg-white rounded-lg shadow transition-shadow duration-300 overflow-hidden h-full flex flex-col ${canAccess ? 'hover:shadow-lg' : 'opacity-75'}`}>
                          <div className="aspect-square relative flex-shrink-0">
                            <ArticleImage article={article} className="w-full h-full" />
                            {!canAccess && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                <Lock className="w-8 h-8 text-white" />
                              </div>
                            )}
                            {article.article_read === false && canAccess && (
                              <div className="absolute top-2 right-2">
                                <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-600 rounded">
                                  {t({ en: "NEW", de: "NEU" })}
                                </span>
                              </div>
                            )}
                          </div>
                        <div className="p-4 flex flex-col flex-grow">
                          <div className="flex-grow">
                            <h3 className="font-semibold text-gray-900 group-hover:text-[#00A3E0] transition-colors line-clamp-2">
                              {cleanText(article.title)}
                            </h3>
                            {article.description && (
                              <p className="text-gray-600 mt-0.5 line-clamp-2">
                                {cleanText(article.description)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                            <span>{formatDate(article.activation)}</span>
                            <LanguageBadge language="de" />
                            <ContractBadge contract={article.contract} />
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A3E0]"></div>
              </div>
            )}

            {/* End of Articles Message */}
            {!hasMore && articles.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {t({ en: "You've reached the end", de: "Ende erreicht" })}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {t({ en: "No articles available at the moment.", de: "Momentan sind keine Artikel verfügbar." })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;